import type { UseInputStateV2Options } from '~kui/composables/useInputState';

import { getInputStateClassesV2 } from '~kui/composables/useInputState';
import { INPUT_STATE_DEFAULT, INPUT_STATE_DISABLED, INPUT_STATE_ERROR, INPUT_STATE_VALID } from '~kui/constants/kui';
import { twMerge } from 'tailwind-merge';
import { computed, ref } from 'vue';

import type { ComboboxProps } from './types';

import { anchorClassList, triggerClassList } from './classList';

export const useComboboxSingle = (props: ComboboxProps, model: Ref<string | number | null>) => {
  const open = ref(false);

  const findOptionFromValue = (value: string | number | null = null) => {
    const searchValue = value ?? model.value;
    if (searchValue) {
      return props.options.find(e => e.value === searchValue);
    }
    return null;
  };

  const selected = computed({
    get() {
      const item = findOptionFromValue();
      if (item) {
        return item.value;
      }
      return null;
    },
    set(value) {
      model.value = value;
    },
  });

  const getDisplayValue = (value: string | number | null = null) => {
    const option = findOptionFromValue(value);
    if (option) {
      return option.label;
    }
    if (selected.value) {
      return selected.value.toString();
    }
    return '';
  };

  const clearSelection = () => {
    model.value = null;
    open.value = true;
  };

  return {
    open,
    selected,
    findOptionFromValue,
    getDisplayValue,
    clearSelection,
  };
};

export const useComboboxMultiple = (props: ComboboxProps, model: Ref<(string | number)[]>) => {
  const open = ref(false);

  const getOptionLabel = (value: string | number) => {
    const option = props.options.find(opt => opt.value === value);
    return option?.label || value.toString();
  };

  const removeItem = (valueToRemove: string | number) => {
    model.value = model.value.filter(value => value !== valueToRemove);
  };

  const clearSelection = () => {
    model.value = [];
    open.value = true;
  };

  return {
    open,
    getOptionLabel,
    removeItem,
    clearSelection,
  };
};

export const useComboboxState = (props: ComboboxProps, hasValue: ComputedRef<boolean>) => {
  const hasError = computed<boolean>(() => !!props.errorMessages?.length);

  const inputState = computed(() => {
    if (props.disabled) {
      return INPUT_STATE_DISABLED;
    }

    if (hasError.value) {
      return INPUT_STATE_ERROR;
    }

    if (hasValue.value) {
      return INPUT_STATE_VALID;
    }

    return INPUT_STATE_DEFAULT;
  });

  return {
    hasError,
    inputState,
  };
};

export const useComboboxClasses = (
  inputState: ComputedRef<typeof INPUT_STATE_ERROR | typeof INPUT_STATE_VALID | typeof INPUT_STATE_DEFAULT | typeof INPUT_STATE_DISABLED>,
  type: 'anchor' | 'trigger' = 'anchor',
) => {
  const classes = computed(() =>
    twMerge(type === 'anchor' ? anchorClassList({ inputState: inputState.value }) : triggerClassList({ inputState: inputState.value })),
  );

  return {
    classes,
  };
};

export const useComboboxClassesV2 = (options: UseInputStateV2Options, type: 'anchor' | 'trigger' = 'anchor') => {
  const { wrapperClasses, elementClasses, fontClasses } = getInputStateClassesV2({
    ...options,
    wrapperClasses: type === 'anchor' ? ['pe-3'] : ['cursor-pointer', 'px-3', 'py-[3px]'],
  });
  return { wrapperClasses, elementClasses, fontClasses };
};

export const useComboboxValue = () => {
  const _hasValue = (v: string | number | null | undefined, nullable = false): boolean => !!v || (nullable && v === null);

  const hasValueSingle = (value: string | number | null | undefined, nullable = false) => computed(() => _hasValue(value, nullable));

  const hasValueMultiple = (value: (string | number)[] | undefined) => computed(() => (value?.length || 0) > 0);

  return {
    _hasValue,
    hasValueSingle,
    hasValueMultiple,
  };
};

export const useComboboxVersionedClasses = (
  props: ComboboxProps,
  model: Ref<string | number | null>,
  type: 'anchor' | 'trigger' = 'anchor',
) => {
  const { hasValueSingle } = useComboboxValue();
  const hasValue = hasValueSingle(model.value, props.nullable);

  const { inputState: v1InputState } = useComboboxState(props, hasValue);
  const { classes: v1WrapperClasses } = useComboboxClasses(v1InputState, type);
  const v1ElementClasses = 'placeholder-muted-foreground h-full min-w-0 flex-1 bg-transparent! py-3.5 ps-3.5 outline-none';

  const {
    wrapperClasses: v2WrapperClasses,
    elementClasses: v2ElementClasses,
    fontClasses: v2FontClasses,
  } = useComboboxClassesV2(
    {
      disabled: computed(() => props.disabled ?? false),
      state: computed(() => props.inputTypeState),
    },
    type,
  );

  const isV2 = computed(() => props.version === 'v2');

  return {
    wrapperClasses: computed(() => (isV2.value ? v2WrapperClasses.value : v1WrapperClasses.value)),
    elementClasses: computed(() => (isV2.value ? v2ElementClasses.value : v1ElementClasses)),
    fontClasses: computed(() => (isV2.value ? v2FontClasses.value : '')),
  };
};

export const useCombobox = (props: ComboboxProps, type: 'single' | 'multiple' = 'single') => {
  const { _hasValue, hasValueSingle, hasValueMultiple } = useComboboxValue();

  if (type === 'single') {
    const model = ref<string | number | null>(null);
    const hasValue = hasValueSingle(model.value, props.nullable);
    const singleUtils = useComboboxSingle(props, model);
    const state = useComboboxState(props, hasValue);
    const classes = useComboboxClasses(state.inputState);

    return {
      model,
      hasValue,
      ...singleUtils,
      ...state,
      ...classes,
      _hasValue,
    };
  }
  const model = ref<(string | number)[]>([]);
  const hasValue = hasValueMultiple(model.value);
  const multipleUtils = useComboboxMultiple(props, model);
  const state = useComboboxState(props, hasValue);
  const classes = useComboboxClasses(state.inputState, 'anchor');

  return {
    model,
    hasValue,
    ...multipleUtils,
    ...state,
    ...classes,
    _hasValue,
  };
};
